/**
 * Menu `routing` values come from the Screen Permissions API and may hold either an
 * in-app path ("/admin/tickets") or a link to another site ("https://example.com").
 * Only in-app paths may be handed to react-router; everything else must open in a new tab.
 */

// Only these schemes are allowed through, so a routing row holding "javascript:" or
// "data:" can never be executed in the app origin.
const ALLOWED_SCHEMES = /^(?:https?|mailto|tel):/i;
const ANY_SCHEME = /^[a-z][a-z0-9+.-]*:/i;
const BARE_HOST = /^[^/\s?#]+\.[a-z]{2,}(?:[:/?#]|$)/i;

/**
 * Returns a fully qualified href when the route points outside the app, otherwise null.
 * Accepts "https://host/x", "http://host", "//host/x", "host.com/x", "www.host.com",
 * "mailto:a@b.com" and "tel:+123".
 */
export function resolveExternalRoute(route) {
  if (typeof route !== 'string') return null;

  const value = route.trim();
  if (!value) return null;

  // In-app path. A leading "//" is protocol-relative, not an app path.
  if (value.startsWith('/') && !value.startsWith('//')) return null;

  if (ANY_SCHEME.test(value)) {
    return ALLOWED_SCHEMES.test(value) ? value : null;
  }
  if (value.startsWith('//')) return `https:${value}`;

  return BARE_HOST.test(value) ? `https://${value}` : null;
}

/**
 * Opens the route in a new tab and returns true when it is external, so callers can
 * do `if (openExternalRoute(routing)) return;` before falling back to navigate().
 */
export function openExternalRoute(route) {
  const href = resolveExternalRoute(route);
  if (!href) return false;

  window.open(href, '_blank', 'noopener,noreferrer');
  return true;
}
